import { defineStore } from 'pinia';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { useUserStore } from 'src/core/stores/user';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { useRequestTracking } from 'src/utils/api/useRequestTracking';

type ApiStore = {
  service: AxiosInstance;
};

/**
 * Represents the api store.
 */
export const useApiStore = defineStore('api', {
  state: () =>
    ({
      service: axios.create({
        baseURL: import.meta.env.VITE_API_URL,
        timeout: -1,
      }),
    } as ApiStore),

  getters: {
    /**
     * Checks if the response is successful.
     * @param response - The AxiosResponse object.
     * @returns True if the response status is 200, false otherwise.
     */
    checkResponseSuccesful:
      () =>
      (response: AxiosResponse | null): boolean => {
        return response?.status === 200;
      },

    /**
     * Checks if the response is specific errorCode.
     * @param response - The AxiosResponse object.
     * @param errorCode - The Axios errorCode to check.
     * @returns True if the response status is the smae as errorCode provided, false otherwise.
     */
    checkErrorResponse:
      () =>
      (response: AxiosResponse | null, errorCode: number): boolean => {
        if (response?.status) {
          return response?.status === errorCode;
        } else {
          return (
            (response as unknown as AxiosError)?.response?.status === errorCode
          );
        }
      },

    /**
     * Unpacks the response to an AxiosError.
     * @param response - The AxiosResponse object.
     * @template T - The type of the response data.
     * @returns The AxiosError object.
     */
    packResponseToAxiosError:
      () =>
      <T>(response: AxiosResponse<T>): AxiosError<T> => {
        return response as unknown as AxiosError<T>;
      },
  },

  actions: {
    /**
     * Creates a request instance and sets up interceptors for authentication and error handling.
     */
    createRequestInstance() {
      const userStore = useUserStore();
      const requestTracking = useRequestTracking();
      // Function that will be called to refresh authorization
      const refreshAuthLogic = async (failedRequest: any) => {
        if (userStore.refresh_token) {
          try {
            const data = await userStore.refreshToken({
              refresh_token: userStore.refresh_token,
            });
            failedRequest.response.config.headers.Authorization = `Bearer ${data?.access_token}`;
            return Promise.resolve(data);
          } catch (error) {
            console.error('Refresh token request failed', error);
            return Promise.reject(error);
          }
        }
        return Promise.reject(failedRequest);
      };

      // Instantiate the interceptor (you can chain it as it returns the axios instance)
      createAuthRefreshInterceptor(this.service, refreshAuthLogic);

      // Request interceptors
      this.service.interceptors.request.use(
        (config) => {
          if (config && config.headers && userStore.access_token) {
            config.headers[
              'Authorization'
            ] = `Bearer ${userStore.access_token}`;
          }

          // Track request for slow request detection
          requestTracking.trackRequestStart(config);

          return config;
        },
        (error) => {
          Promise.reject(error);
        }
      );

      // Response interceptors
      this.service.interceptors.response.use(
        async (response) => {
          // Handle successful response
          requestTracking.handleResponseSuccess(response);
          return response;
        },
        async (error: AxiosError) => {
          // Handle error response
          requestTracking.handleRequestError(error);

          const { response } = error;
          // eslint-disable-next-line no-console
          console.log('onerror', error, response);
          return Promise.reject(error);
        }
      );
    },
  },
});
